/**
 * A control whose feature is not built yet, shown honestly (UI-HONEST,
 * BUILD-PLAN §3.16; decision 50: "controls that pretend to work are treated
 * as defects to fix or honestly disable, never left as mockups").
 *
 * The button is rendered `disabled`, so it cannot be clicked, and beside it
 * is the reason in words: what the control waits for and the build-plan
 * slice (or roadmap item) that brings it. The reason is visible text, not a
 * tooltip, and the button points at it with `aria-describedby`, so a screen
 * reader hears it too. A disabled button is not a dead one: the dead-button
 * scanner clicks only enabled controls (tools/browser-tests/src/page-probe.ts),
 * and the person is told why instead of clicking something that does
 * nothing.
 *
 * The reason comes from the `pending` catalog (lib/i18n/catalogs/
 * controls.en.ts); the slice id is a parameter, isolated left to right
 * (`ltr`), so an Arabic sentence keeps "ED-W2b" whole.
 *
 * Rows that share one reason (every row's Suspend, say) use `PendingNote`
 * once and pass its id as `reasonId`, instead of repeating the sentence in
 * every row.
 */
import { useId, type ReactNode } from "react";
import { Button } from "@/components/ui";
import { useTranslation } from "@/lib/i18n";
import type { controlsEn } from "@/lib/i18n/catalogs/controls.en";
import { ltr } from "@/lib/i18n/format";

/** Why a control waits: a sentence of the `pending` catalog. */
export type PendingReason = keyof typeof controlsEn.pending;

/** The reason as the person reads it, in the active language. */
export function usePendingReason(reason: PendingReason, slice: string): string {
  const { t } = useTranslation();
  return t(`pending.${reason}` as const, { slice: ltr(slice) });
}

/**
 * The reason text. Plain body colour, not the muted grey: muted text on the
 * page's background is below the 4.5:1 contrast small text needs (D60).
 */
const REASON_STYLE = {
  fontSize: 12,
  lineHeight: 1.4,
  color: "var(--atlas-text)",
  maxInlineSize: 280,
  textAlign: "start",
} as const;

export function PendingButton({
  children,
  reason,
  slice,
  reasonId,
  variant,
  size,
}: {
  children: ReactNode;
  /** Why it waits; ignored when `reasonId` points at a shared note. */
  reason?: PendingReason;
  /** The build-plan slice or roadmap item it waits for. */
  slice?: string;
  /** The id of a `PendingNote` that already explains it. */
  reasonId?: string;
  variant?: "primary" | "secondary" | "danger" | "ghost";
  size?: "sm" | "md";
}) {
  const ownId = `${useId()}pending`;
  const { t } = useTranslation();
  const text =
    reasonId === undefined && reason !== undefined
      ? t(`pending.${reason}` as const, { slice: ltr(slice ?? "") })
      : null;
  const describedBy = reasonId ?? (text !== null ? ownId : undefined);
  const button = (
    <Button
      variant={variant}
      size={size}
      disabled
      aria-describedby={describedBy}
    >
      {children}
    </Button>
  );
  if (text === null) return button;
  return (
    <span
      style={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 4,
      }}
    >
      {button}
      <span id={ownId} style={REASON_STYLE}>
        {text}
      </span>
    </span>
  );
}

/** One reason shared by several disabled controls (each row's button). */
export function PendingNote({
  id,
  reason,
  slice,
}: {
  id: string;
  reason: PendingReason;
  slice: string;
}) {
  const text = usePendingReason(reason, slice);
  return (
    <p
      id={id}
      style={{ ...REASON_STYLE, maxInlineSize: "none", marginBlock: "0 12px" }}
    >
      {text}
    </p>
  );
}
